var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Left } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { array, assert, map } from '@codegrade/utils';
import client from '@/client';
import { CoursePermissions as CPerm } from '@/permissions';
import { PeerFeedbackStore, SiteSettingsStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import * as diffMod from '@/utils/diff';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackReplyEdit extends api.CommentReplyEdit {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super(data);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        return new FeedbackReplyEdit(data);
    }
    /** @returns A HTML fragment that can be used to show the difference made in this edit. */
    getDiffHtml() {
        const diff = diffMod.diff(this.oldText, this.newText.orDefault(''));
        return diff
            .map(diffPart => {
            let cls = '';
            if (diffMod.isRemoved(diffPart)) {
                cls = 'removed';
            }
            else if (diffMod.isAdded(diffPart)) {
                cls = 'added';
            }
            let txt = diffPart[1];
            if (cls !== '') {
                /**
                 * Replace the newline chars in the given string with a special character.
                 *
                 * @param match The character to replace the chars in.
                 * @returns The updated string.
                 */
                const innerReplace = (match) => match.replace(/\n/g, `${diffMod.DEFAULT_NEWLINE_CHAR}\n`);
                txt = txt.replace(/^\n+/, innerReplace).replace(/\n+$/, innerReplace);
            }
            return `<span class="${cls}">${utils.htmlEscape(txt)}</span>`;
        })
            .join('');
    }
}
// This should really be kept track of in the store, but that isn't really
// possible for now without rewriting that entire store unfortunately.
// This contains a mapping between serverId and trackingId.
const trackingIdLookup = new Map();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackReply {
    id;
    inReplyToId;
    message;
    author;
    lastEdit;
    replyType;
    createdAt;
    feedbackBaseId;
    commentType;
    approved;
    deleted;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    trackingId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(oldTrackingId, id, inReplyToId, message, author, lastEdit, replyType, createdAt, feedbackBaseId, commentType, approved, deleted) {
        this.id = id;
        this.inReplyToId = inReplyToId;
        this.message = message;
        this.author = author;
        this.lastEdit = lastEdit;
        this.replyType = replyType;
        this.createdAt = createdAt;
        this.feedbackBaseId = feedbackBaseId;
        this.commentType = commentType;
        this.approved = approved;
        this.deleted = deleted;
        const foundTrackingId = trackingIdLookup.get(id ?? -1);
        if (foundTrackingId != null) {
            this.trackingId = foundTrackingId;
        }
        else {
            if (this.id != null) {
                trackingIdLookup.set(this.id, oldTrackingId);
            }
            this.trackingId = oldTrackingId;
        }
        Object.freeze(this);
    }
    /** @returns The id of the author of this feedback. */
    get authorId() {
        return this.author?.id;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, feedbackBaseId, authorLookup, trackingId = utils.getUniqueId()) {
        let author;
        if (data instanceof api.ExtendedNonDeletedCommentReply) {
            author = data.author.extract();
        }
        let comment = '';
        if (!(data instanceof api.DeletedCommentReply)) {
            comment = data.comment;
            if (author == null && data.authorId.isJust()) {
                author = authorLookup.get(data.authorId.value);
            }
        }
        return new FeedbackReply(trackingId, data.id, data.inReplyToId.extractNullable(), comment, author, data.lastEdit.extractNullable(), data.replyType, data.createdAt, feedbackBaseId, data.commentType, data.approved, data.deleted);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty(user, feedbackBaseId, isPeerFeedback = false, inReplyToId = null) {
        return new FeedbackReply(utils.getUniqueId(), null, inReplyToId, '', user, null, 'markdown', SiteSettingsStore.currentTimeGetter()(), feedbackBaseId, isPeerFeedback ? 'peer_feedback' : 'normal', isPeerFeedback, false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isEmpty() {
        return this.message === '';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isPeerFeedback() {
        return this.commentType === 'peer_feedback';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get focusId() {
        if (this.id == null) {
            return '';
        }
        return `${this.feedbackBaseId}-${this.id}`;
    }
    /** @returns Is the current user the author of this reply. */
    get isMyReply() {
        const author = this.author;
        if (author == null) {
            return false;
        }
        const loggedIn = UserStore.loggedInUser().extract();
        return loggedIn != null && utils.user.isEqualOrMemberOf(author, loggedIn);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeEdits(assignment) {
        if (this.isMyReply) {
            return true;
        }
        return (assignment.hasPermission(CPerm.can_view_others_comment_edits) ||
            assignment.hasPermission(CPerm.can_view_others_in_section_comment_edits));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canEdit(assignment) {
        if (this.isMyReply) {
            return true;
        }
        return (assignment.hasPermission(CPerm.can_edit_others_comments) ||
            assignment.hasPermission(CPerm.can_edit_others_in_section_comments));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canApprove(assignment) {
        switch (this.commentType) {
            case 'peer_feedback':
                return assignment.hasPermission(CPerm.can_approve_inline_comments);
            default:
                return false;
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _update(props) {
        return new FeedbackReply(this.trackingId, this.id, 
        // Can't use ?? here because `props.inReplyToId` can be `null`
        // in which case we _do_ want to use the value.
        props.inReplyToId === undefined ? this.inReplyToId : props.inReplyToId, props.message ?? this.message, this.author, this.lastEdit, this.replyType, this.createdAt, this.feedbackBaseId, this.commentType, props.approved ?? this.approved, props.deleted ?? this.deleted);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(message) {
        return this._update({ message });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    markAsDeleted() {
        return this._update({ deleted: true });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    markAsReplyTo(inReplyToId) {
        return this._update({ inReplyToId });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFromApiServerData(data) {
        return FeedbackReply.fromApiServerData(data, this.feedbackBaseId, this.author == null ? new Map() : new Map([[this.author.id, this.author]]), this.trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    approveAndSave(approved) {
        if (this.id == null) {
            return EitherAsync.liftEither(Left(new Error("Cannot change approval of feedback that hasn't been saved.")));
        }
        let meth = client.comment.approveReply;
        if (!approved) {
            meth = client.comment.disapproveReply;
        }
        return meth({
            commentBaseId: this.feedbackBaseId,
            replyId: this.id,
        }).map(response => response.map(cgResult => this.updateFromApiServerData(cgResult)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async fetchEdits() {
        const id = this.id;
        if (id == null) {
            throw new Error("Cannot get edits of feedback that hasn't been saved.");
        }
        return await Array.fromAsync(utils.loadAllPages(nextToken => client.p.comment.getAllReplyEdits({
            commentBaseId: this.feedbackBaseId,
            replyId: id,
            nextToken: nextToken.extract(),
        })), edit => FeedbackReplyEdit.fromApiServerData(edit));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async delete() {
        if (this.id == null) {
            return utils.emptyResponse();
        }
        return await client.p.comment.deleteReply({
            commentBaseId: this.feedbackBaseId,
            replyId: this.id,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async save() {
        if (this.id == null) {
            return await client.p.comment.createReply({
                commentBaseId: this.feedbackBaseId,
                requestBody: {
                    comment: this.message,
                    inReplyTo: Maybe.fromNullable(this.inReplyToId),
                    replyType: this.replyType,
                },
            });
        }
        else {
            return await client.p.comment.patchReply({
                commentBaseId: this.feedbackBaseId,
                replyId: this.id,
                requestBody: {
                    comment: this.message,
                },
            });
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isShowable(showInlineFeedback) {
        switch (showInlineFeedback) {
            case 'all':
                return true;
            case 'only-normal':
                return this.commentType === 'normal';
            case 'only-peer':
                return this.commentType === 'peer_feedback';
            case 'none':
                return false;
            default:
                throw utils.AssertionError.assertNever(showInlineFeedback);
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackThread {
    type;
    id;
    replies;
    workId;
    fileId;
    lineNumber;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(type, id, replies, workId, fileId, lineNumber) {
        this.type = type;
        this.id = id;
        this.replies = replies;
        this.workId = workId;
        this.fileId = fileId;
        this.lineNumber = lineNumber;
        Object.freeze(replies);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, authorLookup) {
        const { type, id } = data;
        const replies = data.replies.map(r => FeedbackReply.fromApiServerData(r, id, authorLookup));
        switch (type) {
            case 'general-feedback':
                return new FeedbackThread(type, id, replies, data.workId, null, null);
            case 'inline-feedback':
                return new FeedbackThread(type, id, replies, data.workId, data.fileId, data.line);
            default:
                throw assert.AssertionError.assertNever(type);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isEmpty() {
        return this.nonDeletedTopLevelReplies.length === 0;
    }
    get replyCount() {
        return array.sumMap(this.replies, r => (r.isEmpty || r.deleted ? 0 : 1));
    }
    get location() {
        switch (this.type) {
            case 'general-feedback':
                return Object.freeze({
                    type: this.type,
                    workId: this.workId,
                });
            case 'inline-feedback':
                assert.AssertionError.assert(this.fileId !== null);
                assert.AssertionError.assert(this.lineNumber !== null);
                return Object.freeze({
                    type: this.type,
                    fileId: this.fileId,
                    lineNumber: this.lineNumber,
                });
            default:
                throw assert.AssertionError.assertNever(this.type);
        }
    }
    get topLevelReplies() {
        return this.replies.filter(r => r.inReplyToId == null);
    }
    get nonDeletedTopLevelReplies() {
        return this.topLevelReplies.filter(r => {
            const { id, deleted } = r;
            return !deleted || (id != null && this.repliesTo.get(id).isJust());
        });
    }
    get repliesTo() {
        const repliesTo = new map.MaybeMap();
        for (const reply of this.replies) {
            const { inReplyToId } = reply;
            if (inReplyToId != null) {
                repliesTo
                    .get(inReplyToId)
                    .ifJust(l => l.push(reply))
                    .ifNothing(() => {
                    repliesTo.set(inReplyToId, [reply]);
                });
            }
        }
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const deleteEmptyThreads = (reply) => {
            const { id, deleted } = reply;
            if (id == null || !deleted) {
                return true;
            }
            const replies = repliesTo.get(id).orDefault([]).filter(deleteEmptyThreads);
            if (replies.length === 0) {
                repliesTo.delete(id);
                return false;
            }
            else {
                return true;
            }
        };
        this.topLevelReplies.forEach(deleteEmptyThreads);
        return repliesTo;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _update(replies) {
        return new FeedbackThread(this.type, this.id, replies, this.workId, this.fileId, this.lineNumber);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addReply(newReply) {
        return this._update(this.replies.concat(newReply));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateReply(updatedReply) {
        const replies = this.replies.map(r => {
            if (r.trackingId === updatedReply.trackingId) {
                return updatedReply;
            }
            return r;
        });
        return this._update(replies);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteReply(deletedReply) {
        if (deletedReply.id == null) {
            return this._update(this.replies.filter(r => r.trackingId !== deletedReply.trackingId));
        }
        else {
            return this.updateReply(deletedReply.markAsDeleted());
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    createReply(user, isPeerFeedback = false, inReplyToId = null) {
        const newReply = FeedbackReply.createEmpty(user, this.id, isPeerFeedback, inReplyToId);
        return this.addReply(newReply);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static canAddReply(assignment, submission, now) {
        const maybeCurrentUser = UserStore.loggedInUser();
        if (maybeCurrentUser.isNothing()) {
            return false;
        }
        const currentUser = maybeCurrentUser.value;
        const author = submission.user;
        const perms = [CPerm.can_grade_work];
        if (utils.user.isEqualOrMemberOf(author, currentUser)) {
            perms.push(CPerm.can_add_own_inline_comments);
        }
        if (perms.some(x => assignment.hasPermission(x))) {
            return true;
        }
        if (assignment.peerFeedbackSettings.isJust()) {
            const userId = maybeCurrentUser.value.id;
            const connections = PeerFeedbackStore.getConnectionsForUser()(assignment, userId);
            connections.ifNothing(() => {
                PeerFeedbackStore.loadConnectionsForUser({
                    userId,
                    assignment,
                });
            });
            const hasConnection = connections
                .map(conns => conns.some(user => user.id === author.id))
                .orDefault(true);
            if (!hasConnection) {
                return false;
            }
            if (!assignment.deadlinePassed(now)) {
                return false;
            }
            if (assignment.peerFeedbackDeadlinePassed(now)) {
                return false;
            }
            return true;
        }
        return false;
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "replyCount", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "location", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "topLevelReplies", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "nonDeletedTopLevelReplies", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "repliesTo", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Feedback {
    general;
    userLines;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    user;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(general, userLines) {
        this.general = general;
        this.userLines = userLines;
        Object.freeze(userLines);
        this.user = Object.freeze(userLines.reduce((acc, line) => {
            const location = line.location;
            if (location.type === 'general-feedback') {
                return acc;
            }
            let fileIdMap = acc[location.fileId];
            if (fileIdMap == null) {
                fileIdMap = {};
                acc[location.fileId] = fileIdMap;
            }
            fileIdMap[location.lineNumber] = line;
            return acc;
        }, {}));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get authorIds() {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getAuthorIds = (thread) => utils.filterMap(thread.replies, reply => Maybe.fromNullable(reply.authorId));
        const authorIds = this.general.reduce((existing, general) => existing.concat(getAuthorIds(general)), utils.flatMap1(this.userLines, getAuthorIds));
        return new Set(authorIds);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        const userLookup = new Map(data.authors?.map(author => [author.id, author]));
        const generalFeedback = data.generalComment.map(gc => FeedbackThread.fromApiServerData(gc, userLookup));
        const userLines = data.user.map(user => FeedbackThread.fromApiServerData(user, userLookup));
        return new Feedback(generalFeedback, userLines);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addFeedbackBase(line) {
        if (line.type !== 'inline-feedback') {
            throw new Error(`The given line does not have the correct type, got ${line.type}`);
        }
        const newLines = [...this.userLines];
        const oldLineIndex = this.userLines.findIndex(l => l.lineNumber === line.lineNumber && l.fileId === line.fileId);
        if (oldLineIndex < 0) {
            newLines.push(line);
        }
        else {
            newLines[oldLineIndex] = line;
        }
        return new Feedback(this.general, newLines);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeFeedbackBase(line) {
        if (line.type !== 'inline-feedback') {
            throw new Error(`The given line does not have the correct type, got ${line.type}`);
        }
        return new Feedback(this.general, this.userLines.filter(l => !(l.lineNumber === line.lineNumber && l.fileId === line.fileId)));
    }
    /**
     * Remove the comments on quiz files from this feedback.
     *
     * @param fileTree The file tree of the submission on which this feedback
     * was placed.
     * @returns A new `Feedback` with all quiz comments removed.
     */
    removeQuizComments(fileTree) {
        const newLines = this.userLines.filter(thread => {
            const loc = thread.location;
            assert.AssertionError.assert(loc.type === 'inline-feedback');
            const file = fileTree.search('student', loc.fileId);
            return file == null || !file.name.endsWith('.cgq');
        });
        return new Feedback(this.general, newLines);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFeedbackLine(fileId, lineNumber) {
        return this.user[fileId][lineNumber];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateThread(thread) {
        if (thread.type === 'general-feedback') {
            return new Feedback(Maybe.of(thread), this.userLines);
        }
        else if (thread.isEmpty) {
            return this.removeFeedbackBase(thread);
        }
        else {
            return this.addFeedbackBase(thread);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    countEntries() {
        return array.sumMap(Object.values(this.user), file => array.sumMap(Object.values(file), feedback => feedback.replyCount), this.general.mapOrDefault(g => g.replyCount, 0));
    }
}
